import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, User, AtSign, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleChange = (val) => {
    setQ(val);
    if (debRef.current) clearTimeout(debRef.current);
    if (val.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true); setOpen(true);
    debRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/social/search?q=${encodeURIComponent(val.trim())}`);
        setResults(res.data.users || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
  };

  const goto = (username) => {
    setOpen(false); setQ("");
    navigate(`/app/u/${username}`);
  };

  return (
    <div ref={boxRef} className="user-search" data-testid="universal-search" style={{ position: "relative", width: "min(420px, 100%)" }}>
      <div style={{ position: "relative" }}>
        <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
        <input
          data-testid="universal-search-input"
          value={q}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
          placeholder="Search people by username or name…"
          style={{
            width: "100%", padding: "0.55rem 0.8rem 0.55rem 2.2rem", borderRadius: 999,
            border: "1px solid var(--border-main)", background: "var(--bg-surface)",
            color: "var(--text-primary)", fontSize: "0.88rem", outline: "none",
          }}
        />
      </div>
      {open && (
        <div className="card" data-testid="search-results" style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 50,
          padding: "0.4rem", maxHeight: 340, overflowY: "auto",
        }}>
          {loading ? (
            <div style={{ padding: "0.8rem", display: "flex", alignItems: "center", gap: 8, color: "var(--text-secondary)" }}>
              <Loader2 size={15} className="spin" /> Searching…
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: "0.8rem", color: "var(--text-muted)", fontSize: "0.85rem" }}>No people found.</div>
          ) : results.map((u) => (
            <button key={u.username} data-testid={`search-result-${u.username}`}
              onClick={() => goto(u.username)}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: "0.7rem", padding: "0.55rem 0.7rem",
                background: "transparent", border: "none", borderRadius: 8, cursor: "pointer", textAlign: "left",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--border-main)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--border-main)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : <User size={16} />}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "0.88rem", fontWeight: 500 }}>{u.name}</div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 2 }}>
                  <AtSign size={12} />{u.username}{u.headline ? ` · ${u.headline}` : ""}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
